import { FBLive } from '@/types/fb-type';
import {
  liveMatchesPostId,
  objectIdFromFbId,
  parseLiveTimeMs,
  sortLivesForSidebar,
  withEffectiveStatus,
} from './live-status';

/** Socket/API rows often lack creationTime; remembered lives must stay promotable. */
function ensureLiveMetadata(live: FBLive): FBLive {
  const candidates = [live.creationTime, live.broadcastStartTime];
  const creationTime =
    candidates.find((t) => t && !Number.isNaN(parseLiveTimeMs(t))) ?? new Date().toISOString();
  return { ...live, creationTime, status: 'LIVE' };
}

const STORAGE_KEY = 'sale.activeLive.v1';

const activeLiveKeys = new Set<string>();
const activeLiveSnapshots = new Map<string, FBLive>();

function addKey(key: string) {
  if (!key) return;
  activeLiveKeys.add(key);
  activeLiveKeys.add(objectIdFromFbId(key));
}

function persist() {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        keys: [...activeLiveKeys],
        snapshots: [...activeLiveSnapshots.values()],
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

function hydrate() {
  if (activeLiveSnapshots.size > 0) return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { keys?: string[]; snapshots?: FBLive[] };
    for (const key of parsed.keys ?? []) addKey(key);
    for (const live of parsed.snapshots ?? []) {
      if (!live?.id) continue;
      activeLiveSnapshots.set(live.id, ensureLiveMetadata(live));
      addKey(live.id);
      if (live.postId) addKey(live.postId);
    }
  } catch {
    // ignore corrupt storage
  }
}

hydrate();

export function rememberActiveLive(live: FBLive) {
  hydrate();
  addKey(live.id);
  if (live.postId) addKey(live.postId);
  activeLiveSnapshots.set(live.id, ensureLiveMetadata(live));
  persist();
}

export function getActiveLiveSnapshots(): FBLive[] {
  hydrate();
  return [...activeLiveSnapshots.values()];
}

export function getPrimaryActiveLive(): FBLive | null {
  const snapshots = getActiveLiveSnapshots();
  if (snapshots.length === 0) return null;
  return sortLivesForSidebar(snapshots)[0] ?? null;
}

export function clearActiveLive() {
  activeLiveKeys.clear();
  activeLiveSnapshots.clear();
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Merge API lives with remembered socket-discovered lives; demote stale LIVE rows. */
export function mergeLivesWithRemembered(apiLives: FBLive[]) {
  hydrate();
  const byId = new Map<string, FBLive>();
  for (const live of apiLives.map(withEffectiveStatus)) {
    byId.set(live.id, live);
  }

  for (const snapshot of getActiveLiveSnapshots()) {
    const match = [...byId.values()].find(
      (live) => liveMatchesPostId(live, snapshot.postId ?? snapshot.id) || live.id === snapshot.id,
    );
    if (!match) {
      byId.set(snapshot.id, ensureLiveMetadata(snapshot));
    } else {
      byId.set(
        match.id,
        ensureLiveMetadata({
          ...match,
          postId: match.postId || snapshot.postId,
          title: match.title || snapshot.title || 'Live now',
          creationTime: match.creationTime || snapshot.creationTime,
          broadcastStartTime: match.broadcastStartTime || snapshot.broadcastStartTime,
        }),
      );
    }
  }

  return sortLivesForSidebar([...byId.values()]);
}

export function upsertLiveIntoList(lives: FBLive[], input: { postId: string; title?: string }) {
  const { postId } = input;
  if (!postId) return lives;

  const existing = lives.find((live) => liveMatchesPostId(live, postId));
  if (existing) {
    const updated = ensureLiveMetadata({
      ...existing,
      postId: existing.postId || postId,
      title: existing.title || input.title || 'Live now',
    });
    rememberActiveLive(updated);
    const rest = lives.filter((live) => live.id !== existing.id);
    return sortLivesForSidebar([updated, ...rest.map(withEffectiveStatus)]);
  }

  const synthetic = ensureLiveMetadata({
    id: objectIdFromFbId(postId),
    postId,
    title: input.title || 'Live now',
    status: 'LIVE',
    liveViews: null,
    creationTime: new Date().toISOString(),
    broadcastStartTime: null,
    permalinkUrl: '',
    streamUrl: null,
    secureStreamUrl: null,
    commentCount: 0,
    reactionCount: 0,
    previewUrl: null,
    description: null,
  });
  rememberActiveLive(synthetic);
  return sortLivesForSidebar([synthetic, ...lives.map(withEffectiveStatus)]);
}
